import React from 'react';
import propTypes from 'prop-types';
import { Breadcrumb as Bootcrumb, BreadcrumbItem } from 'reactstrap';

import Can from 'app/Can/container';
import CanvasMenu from 'app/CanvasMenu/container';
import { generateRenderingEnvironmentCaptions } from 'app/RenderingEnvironment/helper';

import { REAL_ROOT_FOLDER_ID } from 'lib/const';
import { noop } from 'lib/noop';

import styles from './Breadcrumb.css';
import EditRenderingEnvironment from './EditRenderingEnvironment';

/**
 * render a breadcrumb to the current working directory
 * @param {object} props - component's props
 * @returns {React.Component} the breadcrumb component
 */
const Breadcrumb = ({
  changePagePropertyDetail,
  checkStatusAndVisitFolder,
  cwdDetails,
  isNewStatus,
  isPage,
  pageTemplateName,
  pageType,
  setRenderingEnv,
  userHomeFolderId,
  visitFolder
}) => {
  const { Id, Breadcrumb = [], Name = 'Loading...' } = cwdDetails;
  const breadcrumb = [...Breadcrumb, { Id, Name }];

  // don't show real root unless the user prefers it
  let home = breadcrumb.shift();
  if (home?.Id === REAL_ROOT_FOLDER_ID && userHomeFolderId !== REAL_ROOT_FOLDER_ID) {
    home = breadcrumb.shift();
  }

  return (
    <div className={`d-flex ${styles.crumb} ${styles.wrapper}`}>
      <Bootcrumb
        className="d-flex align-items-center mr-auto"
        tag="nav"
        listClassName={`mb-0 ${styles.crumb}`}
      >
        {home && (
          <BreadcrumbItem
            active={!Id}
            data-test-id={`breadcrumb-${home.Id}`}
            tag={!Id ? 'span' : 'a'}
            href=""
            onClick={e => {
              e.preventDefault();
              checkStatusAndVisitFolder(home.Id);
            }}
          >
            {home.Name}
          </BreadcrumbItem>
        )}
        {breadcrumb.map((node, index) => {
          const isLast = index === breadcrumb.length - 1;
          return (
            <BreadcrumbItem
              active={isLast}
              data-test-id={`breadcrumb-${node.Id}`}
              key={node.Id}
              tag={!isLast ? 'a' : 'span'}
              href=""
              onClick={e => {
                e.preventDefault();
                if (!isLast) {
                  visitFolder(node.Id);
                }
              }}
            >
              {node.Name}
            </BreadcrumbItem>
          );
        })}
      </Bootcrumb>
      <div className="d-flex align-items-center">
        <EditRenderingEnvironment
          className="mr-3"
          renderingEnvCaption={generateRenderingEnvironmentCaptions(
            pageType,
            pageTemplateName
          )}
          pageType={pageType}
          pageTemplateName={pageTemplateName}
          changePagePropertyDetail={changePagePropertyDetail}
          isPage={isPage}
          isNewStatus={isNewStatus}
          setRenderingEnv={setRenderingEnv}
        />
        <Can read atLeastOne report>
          <div className="mr-3">
            <CanvasMenu />
          </div>
        </Can>
      </div>
    </div>
  );
};

Breadcrumb.propTypes = {
  cwdDetails: propTypes.shape({
    Breadcrumb: propTypes.arrayOf(
      propTypes.shape({
        Id: propTypes.string,
        Name: propTypes.string
      })
    ),
    Children: propTypes.arrayOf(
      propTypes.shape({
        Id: propTypes.string,
        Name: propTypes.string,
        Type: propTypes.string
      })
    ),
    DateCreated: propTypes.string,
    DateModified: propTypes.string,
    Id: propTypes.string,
    Name: propTypes.string
  }),
  changePagePropertyDetail: propTypes.func.isRequired,
  checkStatusAndVisitFolder: propTypes.func.isRequired,
  isNewStatus: propTypes.bool,
  isPage: propTypes.bool,
  pageTemplateName: propTypes.string,
  pageType: propTypes.string,
  setRenderingEnv: propTypes.func,
  userHomeFolderId: propTypes.string.isRequired,
  visitFolder: propTypes.func.isRequired
};

Breadcrumb.defaultProps = {
  cwdDetails: {},
  isNewStatus: true,
  isPage: false,
  pageTemplateName: undefined,
  pageType: undefined,
  setRenderingEnv: noop
};

export default Breadcrumb;
